import { useEffect, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowClockwiseBold,
    PiChatCircleTextBold,
    PiCopyBold,
    PiDatabaseBold,
    PiEnvelopeSimpleBold,
    PiFactoryBold,
    PiLightbulbFilamentBold,
    PiListChecksBold,
    PiQuestionBold,
    PiWarningCircleFill,
} from 'react-icons/pi';
import { Button } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { AI_BUTTON, AiDisclaimer, AiError, AiPlateIcon, AiThinking, aiErrorMessage, useAiStatus } from './aiShared';

type PlanItem = { ref: string; text: string };
type PlanAction = {
    owner: 'check' | 'seller' | 'supplier' | 'registry';
    title: string;
    items: PlanItem[];
    order_number?: string;
    seller?: string | null;
    client?: string | null;
    message?: string;
};

type Review = {
    status: 'ok' | 'corrigir' | 'critico';
    summary: string;
    next_steps?: string[];
    patterns?: { id: string; kind: string; title: string; detail: string }[];
    actions?: PlanAction[];
    issues: { severity?: 'alta' | 'media' | 'baixa'; title?: string; detail?: string; refs?: string[] }[];
    email: { subject: string; body: string };
    generated_at: string;
    cached: boolean;
};

const STATUS = {
    ok: { label: 'PI pode ser aprovada', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300' },
    corrigir: { label: 'Pedir correção', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200' },
    critico: { label: 'Não aprovar a PI', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300' },
};

const SEVERITY = {
    alta: 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-100',
    media: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100',
    baixa: 'border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100',
};

const OWNER: Record<PlanAction['owner'], { label: string; icon: ReactNode; cls: string }> = {
    check: { label: 'Confirmar antes', icon: <PiQuestionBold />, cls: 'border-violet-200 bg-violet-50/60 dark:border-violet-500/30 dark:bg-violet-500/10' },
    seller: { label: 'Vendedor', icon: <PiChatCircleTextBold />, cls: 'border-sky-200 bg-sky-50/60 dark:border-sky-500/30 dark:bg-sky-500/10' },
    supplier: { label: 'Fornecedor', icon: <PiFactoryBold />, cls: 'border-amber-200 bg-amber-50/60 dark:border-amber-500/30 dark:bg-amber-500/10' },
    registry: { label: 'Cadastro / PO', icon: <PiDatabaseBold />, cls: 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800' },
};

async function copyText(text: string, ok: string) {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(ok);
    } catch {
        toast.error('Não foi possível copiar.');
    }
}

function ActionCard({ action, onOpenEmail }: { action: PlanAction; onOpenEmail: () => void }) {
    const [message, setMessage] = useState(action.message ?? '');
    const [expanded, setExpanded] = useState(action.items.length <= 6);
    const owner = OWNER[action.owner];
    const items = expanded ? action.items : action.items.slice(0, 6);

    return (
        <section className={cn('rounded-xl border p-3', owner.cls)}>
            <header className="mb-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-gray-700 ring-1 ring-black/5 dark:bg-gray-900 dark:text-gray-200">
                    {owner.icon} {owner.label}
                </span>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{action.title}</h4>
                {action.seller && (
                    <span className="text-xs text-gray-500">
                        {action.seller}
                        {action.client ? ` · ${action.client}` : ''}
                    </span>
                )}
                <span className="ml-auto text-[11px] font-semibold text-gray-500">{action.items.length} item(ns)</span>
            </header>
            <ul className="space-y-1 text-xs text-gray-800 dark:text-gray-200">
                {items.map((i, idx) => (
                    <li key={idx} className="flex gap-2">
                        <span className="shrink-0 font-mono text-[11px] font-semibold text-gray-500">{i.ref}</span>
                        <span>{i.text}</span>
                    </li>
                ))}
            </ul>
            {action.items.length > 6 && (
                <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-1 text-[11px] font-semibold text-primary hover:underline">
                    {expanded ? 'Mostrar menos' : `Mostrar os ${action.items.length}`}
                </button>
            )}
            {action.owner === 'seller' && action.message && (
                <div className="mt-3 space-y-1.5">
                    <p className="text-[11px] font-semibold text-gray-500">Mensagem para o vendedor (edite se quiser)</p>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="h-36 w-full rounded-lg border border-gray-200 bg-white p-2 text-xs leading-relaxed dark:border-gray-700 dark:bg-gray-900"
                        aria-label="Mensagem para o vendedor"
                    />
                    <Button size="sm" variant="outline" onClick={() => copyText(message, 'Mensagem copiada.')}>
                        <PiCopyBold className="me-1.5 size-4" /> Copiar mensagem
                    </Button>
                </div>
            )}
            {(action.owner === 'supplier' || action.owner === 'check') && (
                <button type="button" onClick={onOpenEmail} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline">
                    <PiEnvelopeSimpleBold /> Está no e-mail ao fornecedor
                </button>
            )}
        </section>
    );
}

export default function ProformaAiReviewButton({ shipmentId }: { shipmentId: number }) {
    const ai = useAiStatus();
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<'plan' | 'review' | 'email'>('plan');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const gen = useMutation({
        mutationFn: async (refresh: boolean) =>
            (await api.post<{ data: Review }>(`/ai/shipments/${shipmentId}/proforma-review`, { refresh })).data.data,
    });

    useEffect(() => {
        if (gen.data) {
            setSubject(gen.data.email.subject);
            setBody(gen.data.email.body);
        }
    }, [gen.data]);

    if (!ai.data?.features.purchase_orders) return null;
    const r = gen.data;

    const copy = () => copyText(`${subject}\n\n${body}`, 'E-mail copiado.');

    return (
        <>
            <button
                type="button"
                title="Resume as divergências e escreve o e-mail ao fornecedor"
                onClick={() => {
                    setOpen(true);
                    setTab('plan');
                    gen.mutate(false);
                }}
                className={cn(AI_BUTTON, 'px-3 py-2 text-sm')}
            >
                <AiPlateIcon className="size-5" /> Conferir com IA
            </button>
            <V2Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Conferência PI × PO com IA"
                description="O que fazer e com quem: padrões, pendências por responsável e mensagens prontas."
                size="5xl"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Fechar
                        </Button>
                        <Button variant="outline" disabled={gen.isPending} onClick={() => gen.mutate(true)}>
                            <PiArrowClockwiseBold className="me-1.5 size-4" /> Conferir de novo
                        </Button>
                        {r && tab === 'email' && (
                            <Button onClick={copy}>
                                <PiCopyBold className="me-1.5 size-4" /> Copiar e-mail
                            </Button>
                        )}
                    </>
                }
            >
                {gen.isPending ? (
                    <AiThinking label="Comparando a PI linha a linha com a PO…" />
                ) : gen.isError ? (
                    <AiError message={aiErrorMessage(gen.error)} onRetry={() => gen.mutate(false)} />
                ) : r ? (
                    <div className="space-y-4">
                        <div className="flex gap-1 rounded-lg bg-gray-100 p-1 text-xs font-semibold dark:bg-gray-800">
                            {[
                                ['plan', 'Plano de ação'],
                                ['email', 'E-mail ao fornecedor (inglês)'],
                                ['review', 'Divergências'],
                            ].map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setTab(key as 'plan' | 'review' | 'email')}
                                    className={cn(
                                        'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5',
                                        tab === key ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-100' : 'text-gray-500',
                                    )}
                                >
                                    {key === 'email' && <PiEnvelopeSimpleBold className="size-3.5" />}
                                    {key === 'plan' && <PiListChecksBold className="size-3.5" />}
                                    {label}
                                </button>
                            ))}
                        </div>

                        {tab === 'plan' ? (
                            <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1 text-sm">
                                <div>
                                    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', STATUS[r.status].cls)}>{STATUS[r.status].label}</span>
                                    <p className="mt-2 text-xs leading-relaxed text-gray-700 dark:text-gray-200">{r.summary}</p>
                                </div>
                                {(r.next_steps ?? []).length > 0 && (
                                    <ol className="space-y-1 rounded-xl border border-gray-200 bg-white p-3 text-xs dark:border-gray-700 dark:bg-gray-900">
                                        {r.next_steps!.map((step, idx) => (
                                            <li key={idx} className="flex gap-2">
                                                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-gray-900 text-[10px] font-bold text-white dark:bg-gray-100 dark:text-gray-900">
                                                    {idx + 1}
                                                </span>
                                                <span className="pt-0.5 text-gray-800 dark:text-gray-200">{step}</span>
                                            </li>
                                        ))}
                                    </ol>
                                )}
                                {(r.patterns ?? []).map((p) => (
                                    <div key={p.id} className="flex gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-100">
                                        <PiLightbulbFilamentBold className="mt-0.5 size-4 shrink-0" />
                                        <div>
                                            <p className="font-semibold">{p.title}</p>
                                            <p className="opacity-90">{p.detail}</p>
                                        </div>
                                    </div>
                                ))}
                                {(r.actions ?? []).map((a, idx) => (
                                    <ActionCard key={`${a.owner}-${a.order_number ?? idx}`} action={a} onOpenEmail={() => setTab('email')} />
                                ))}
                                {(r.actions ?? []).length === 0 && (
                                    <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-4 text-center text-xs text-emerald-800">
                                        Nada a fazer: PO e PI batem.
                                    </p>
                                )}
                            </div>
                        ) : tab === 'review' ? (
                            <div className="space-y-3 text-sm">
                                <div>
                                    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', STATUS[r.status].cls)}>{STATUS[r.status].label}</span>
                                    <p className="mt-2 text-xs leading-relaxed text-gray-700 dark:text-gray-200">{r.summary}</p>
                                </div>
                                <div className="max-h-[48vh] space-y-2 overflow-y-auto">
                                    {r.issues.map((i, idx) => (
                                        <div key={idx} className={cn('rounded-lg border px-3 py-2', SEVERITY[i.severity ?? 'media'])}>
                                            <p className="flex items-center gap-1.5 text-xs font-semibold">
                                                <PiWarningCircleFill className="size-4 shrink-0" /> {i.title}
                                            </p>
                                            <p className="mt-0.5 text-xs opacity-90">{i.detail}</p>
                                            {(i.refs ?? []).length > 0 && (
                                                <p className="mt-1 flex flex-wrap gap-1">
                                                    {i.refs!.map((ref) => (
                                                        <span key={ref} className="rounded bg-white/70 px-1.5 py-0.5 font-mono text-[10px] dark:bg-black/20">
                                                            {ref}
                                                        </span>
                                                    ))}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <input
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold dark:border-gray-700 dark:bg-gray-900"
                                    aria-label="Assunto"
                                />
                                <textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    className="h-[46vh] w-full rounded-lg border border-gray-200 bg-white p-3 font-mono text-xs leading-relaxed dark:border-gray-700 dark:bg-gray-900"
                                    aria-label="Corpo do e-mail"
                                />
                                <p className="text-[11px] text-gray-400">
                                    A lista de correções é montada pelo Hub (completa, linha a linha); a IA só escreve a introdução e o fechamento. Edite à vontade antes de copiar.
                                </p>
                            </div>
                        )}
                        <AiDisclaimer generatedAt={r.generated_at} cached={r.cached} />
                    </div>
                ) : null}
            </V2Modal>
        </>
    );
}
